import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, List } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { ProcessSummary } from "../../api/types";
import ProcessSearchList from "./ProcessSearchList";
import {
  PROCESS_DIRECTORY_URL,
  readRecentProcessSlugs,
  rememberProcess,
} from "./processPreferences";

export default function ProcessSwitcher({
  process,
  processes,
  onSelect,
  people = {},
  viewerId = "local",
}: {
  process: { slug: string; name: string };
  processes: ProcessSummary[];
  onSelect: (process: ProcessSummary) => void;
  people?: Record<string, string>;
  viewerId?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useEffect(() => rememberProcess(viewerId, process.slug), [viewerId, process.slug]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <h1 className="min-w-0 max-w-full">
        <Popover.Trigger asChild>
          <button
            type="button"
            title={t("switch_process")}
            className="inline-flex min-w-0 max-w-full items-center gap-2 rounded px-1 py-1 text-left text-text hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className="min-w-0 truncate text-lg font-medium tracking-tight">
              {process.name}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-text-dim" aria-hidden="true" />
          </button>
        </Popover.Trigger>
      </h1>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          aria-label={t("switch_process")}
          className="pd-theme z-50 flex max-h-[min(36rem,var(--radix-popover-content-available-height))] w-[28rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-outline bg-surface shadow-lift"
        >
          <ProcessSearchList
            processes={processes}
            people={people}
            recent={readRecentProcessSlugs(viewerId)}
            current={process.slug}
            onSelect={entry => {
              setOpen(false);
              if (entry.slug !== process.slug) onSelect(entry);
            }}
          />
          <div className="shrink-0 border-t border-outline p-1.5">
            <Link
              to={PROCESS_DIRECTORY_URL}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-text-mid hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-primary"
            >
              <List className="h-4 w-4" aria-hidden="true" />
              {t("browse_all_processes")}
            </Link>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
